import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import OTPVerification from '../components/OTPVerification';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);
  const navigate = useNavigate();
  const { login, verifyLoginOTP } = useAuth();

  const getErrorMessage = (error) => {
    const errorMessage = error?.message?.toLowerCase() || '';
    console.log('Processing error message:', errorMessage);
    
    if (errorMessage.includes('invalid-credential') || errorMessage.includes('invalid email or password')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (errorMessage.includes('user-not-found') || errorMessage.includes('no account found')) {
      return 'No account found with this email address. Please check your email or sign up for a new account.';
    }
    if (errorMessage.includes('wrong-password')) {
      return 'Incorrect password. Please check your password and try again.';
    }
    if (errorMessage.includes('too-many-requests')) {
      return 'Too many failed login attempts. Please try again later or reset your password.';
    }
    if (errorMessage.includes('network-request-failed')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (errorMessage.includes('suspended')) {
      return 'Your account has been suspended. Please contact support for assistance.';
    }
    return 'Unable to sign in. Please try again or contact support if the problem persists.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      console.log('Starting login process...');
      const result = await login(email, password);
      console.log('Login result:', result);
      
      // If admin user, redirect to admin dashboard
      if (result.success && result.isAdmin) {
        console.log('Admin login successful, redirecting...');
        navigate('/admin');
        return;
      }

      // For regular users, show OTP verification
      console.log('Regular user login, showing OTP...');
      setTempUserData(result);
      setShowOTP(true);
    } catch (err) {
      console.error('Login error:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPVerify = async (otp) => {
    try {
      await verifyLoginOTP(tempUserData, otp);
      navigate('/dashboard');
    } catch (err) {
      throw new Error('Invalid OTP code. Please try again.');
    }
  };

  const handleOTPCancel = () => {
    setShowOTP(false);
    setTempUserData(null);
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 relative">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_0%,rgba(34,211,238,0.12),transparent_55%),radial-gradient(700px_circle_at_90%_20%,rgba(56,189,248,0.08),transparent_55%)]" />
      <Navigation />
      {showOTP ? (
        <OTPVerification
          type="login"
          onVerify={handleOTPVerify}
          onCancel={handleOTPCancel}
        />
      ) : (
        <div className="relative flex items-center justify-center px-4 py-20 min-h-[80vh]">
          <motion.div
            className="w-full max-w-md card-glass rounded-3xl border border-white/10 p-8 shadow-[0_18px_60px_rgba(0,0,0,0.45)]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col items-center mb-8">
              <Logo darkMode={true} />
              <h2 className="mt-6 text-center text-3xl font-semibold tracking-tight text-white">
                Sign in to your account
              </h2>
            </div>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl bg-rose-500/10 p-4 border border-rose-400/25 mb-4"
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationCircleIcon className="h-5 w-5 text-rose-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-rose-200">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-stone-300 mb-1">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    className="input-glass mt-1 block w-full placeholder:text-stone-500"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-stone-300 mb-1">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    className="input-glass mt-1 block w-full placeholder:text-stone-500"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <Link to="/forgot-password" className="font-medium text-cyan-300 hover:text-cyan-200">
                    Forgot your password?
                  </Link>
                </div>
              </div>
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex justify-center rounded-xl bg-cyan-400 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </motion.button>
              <div className="text-center">
                <p className="text-sm text-stone-400">
                  Don't have an account?{' '}
                  <Link to="/signup" className="font-medium text-cyan-300 hover:text-cyan-200">
                    Sign up
                  </Link>
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default Login; 